<script setup lang="ts">
import { ensureHasAccess, useOnboardedEffect } from '@peanuds/app-shell';
import { useAccountSelection, useUserAccountsStore } from '@peanuds/shared-state';
import { useTranslation } from 'i18next-vue';
import { storeToRefs } from 'pinia';
import type { Swiper } from 'swiper';
import { register } from 'swiper/element/bundle';
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import IbanSwiperControls from './components/IbanSwiperControls.vue';
import IbanSwiperSlide from './components/IbanSwiperSlide.vue';
import { UiButton } from '@peanuds/ui';

interface Props {
  allowMultipleSelection?: boolean;
}

const { t } = useTranslation();
const { allowMultipleSelection = false } = defineProps<Props>();

register();

interface IbanSlide {
  id: string;
  label: string;
  currency?: string;
  balance?: number;
  isPending?: boolean;
  variant?: 'default' | 'multiple-currencies';
}

const expanded = defineModel<boolean>({ default: false });

const userAccountsStore = useUserAccountsStore();
const { ibanAccounts: accounts, isLoading } = storeToRefs(userAccountsStore);
const { ensureAccountsLoaded } = userAccountsStore;
const { selectedAccountIds, isSelected, toggleAccountSelection, selectSingleAccount } =
  useAccountSelection();

useOnboardedEffect(() => {
  if (accounts.value.length > 0 || !ensureHasAccess()) {
    return;
  }

  return ensureAccountsLoaded();
});

watch(
  [() => allowMultipleSelection, () => accounts.value, () => selectedAccountIds.value],
  ([multipleSelectionEnabled, accountList, accountIds]) => {
    if (multipleSelectionEnabled) {
      return;
    }

    const selectableAccountIds = new Set(
      accountList.filter((account) => account.status !== 'pending').map((account) => account.id),
    );

    const selectedSelectableAccountId = accountIds.find((accountId) => {
      return selectableAccountIds.has(accountId);
    });

    const firstSelectableAccountId = accountList.find((account) => {
      return selectableAccountIds.has(account.id);
    })?.id;

    const nextAccountId = selectedSelectableAccountId ?? firstSelectableAccountId;

    if (!nextAccountId) {
      return;
    }

    if (accountIds.length === 1 && accountIds[0] === nextAccountId) {
      return;
    }

    selectSingleAccount(nextAccountId);
  },
  { immediate: true },
);

const getAccountLabel = (account: (typeof accounts.value)[number]) => {
  if (account.mainAccountId === null) {
    const alias = typeof account.alias === 'string' ? account.alias.trim() : '';
    return alias || t('ibanSwiper.labels.main');
  }

  const alias = typeof account.alias === 'string' ? account.alias.trim() : '';
  return alias || 'Account';
};

const slides = computed<IbanSlide[]>(() => {
  const accountSlides: IbanSlide[] = accounts.value.map((account) => ({
    id: account.id,
    label: getAccountLabel(account),
    currency: account.currency,
    balance: account.availableBalance ?? 0,
    isPending: account.status === 'pending',
    variant: 'default',
  }));

  return [
    ...accountSlides,
    {
      id: 'multiple-currencies',
      label: t('ibanSwiper.labels.multiple_currencies'),
      variant: 'multiple-currencies',
    },
  ];
});

type SwiperElement = HTMLElement & { swiper?: Swiper };
const swiperRef = useTemplateRef<SwiperElement>('ibanSwiperRef');
const showSwiperControls = ref(false);

const updateSwiperControlsVisibility = (swiper: Swiper) => {
  showSwiperControls.value = !swiper.isLocked;
};

const updateSwiperLayout = async () => {
  await nextTick();

  const swiper = swiperRef.value?.swiper;
  if (!swiper) {
    return;
  }

  swiper.update();
  updateSwiperControlsVisibility(swiper);
};

const handleSwiperInit = (event: Event) => {
  const swiper = (event as CustomEvent<[Swiper]>).detail[0];
  updateSwiperControlsVisibility(swiper);
};

const hideSwiperControls = () => {
  showSwiperControls.value = false;
};

const showSwiperNavigationControls = () => {
  showSwiperControls.value = true;
};

const handleWindowResize = () => {
  void updateSwiperLayout();
};

onMounted(() => {
  void updateSwiperLayout();
  window.addEventListener('resize', handleWindowResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleWindowResize);
});

watch([() => slides.value.length, isLoading, expanded], updateSwiperLayout);

const collapsibleIcon = computed(() =>
  expanded.value ? 'pi pi-angle-double-up' : 'pi pi-angle-double-down',
);

const toggleExpanded = () => {
  expanded.value = !expanded.value;
};

const handleSlide = (direction: 'prev' | 'next') => {
  const swiper = swiperRef.value?.swiper;
  if (!swiper) return;

  if (direction === 'prev') {
    swiper.slidePrev();
  } else {
    swiper.slideNext();
  }
};

const handlePrev = () => handleSlide('prev');
const handleNext = () => handleSlide('next');

const isPendingSlide = (slide: IbanSlide) => slide.isPending === true;

const isSelectableSlide = (slide: IbanSlide) => {
  return slide.variant !== 'multiple-currencies' && !isPendingSlide(slide);
};

const handleSelectSlide = (slide: IbanSlide) => {
  if (!isSelectableSlide(slide)) {
    return;
  }

  if (!allowMultipleSelection) {
    selectSingleAccount(slide.id);
    return;
  }

  toggleAccountSelection(slide.id);
};

const getSlideAriaLabel = (slide: IbanSlide) => {
  if (!isSelectableSlide(slide)) {
    return undefined;
  }

  const isCurrentlySelected = isSelected(slide.id);

  if (!allowMultipleSelection) {
    return isCurrentlySelected
      ? t('ibanSwiper.aria.cannot_deselect_last_account', { account: slide.label })
      : t('ibanSwiper.aria.select_account', { account: slide.label });
  }

  if (!isCurrentlySelected) {
    return t('ibanSwiper.aria.select_account', { account: slide.label });
  }

  if (selectedAccountIds.value.length === 1) {
    return t('ibanSwiper.aria.cannot_deselect_last_account', { account: slide.label });
  }

  return t('ibanSwiper.aria.deselect_account', { account: slide.label });
};
</script>

<template>
  <div
    :class="{ 'iban-swiper': true, 'iban-swiper--expanded': expanded }"
    :aria-expanded="expanded"
    data-testid="dashboard-iban-swiper"
  >
    <div class="iban-swiper__viewport">
      <swiper-container
        ref="ibanSwiperRef"
        class="iban-swiper__container"
        space-between="20"
        slides-per-view="auto"
        free-mode="true"
        watch-overflow="true"
        @swiperinit="handleSwiperInit"
        @swiperlock="hideSwiperControls"
        @swiperunlock="showSwiperNavigationControls"
      >
        <template v-if="isLoading">
          <swiper-slide
            v-for="i in 2"
            :key="`iban-skeleton-${i}`"
            :class="{ 'iban-swiper__slide': true, 'iban-swiper__slide--expanded': expanded }"
            aria-hidden="true"
          >
            <div class="iban-swiper__skeleton">
              <div class="iban-swiper__skeleton-row">
                <Skeleton class="iban-swiper__skeleton-line iban-swiper__skeleton-line--label" />
                <Skeleton class="iban-swiper__skeleton-line iban-swiper__skeleton-line--chip" />
              </div>

              <div class="iban-swiper__skeleton-row iban-swiper__skeleton-row--bottom">
                <Skeleton class="iban-swiper__skeleton-line iban-swiper__skeleton-line--iban" />
                <Skeleton class="iban-swiper__skeleton-line iban-swiper__skeleton-line--balance" />
              </div>
            </div>
          </swiper-slide>
        </template>

        <template v-else>
          <swiper-slide
            v-for="slide in slides"
            :key="slide.id"
            :class="{
              'iban-swiper__slide': true,
              'iban-swiper__slide--expanded': expanded,
              'iban-swiper__slide--currencies': slide.variant === 'multiple-currencies',
              'iban-swiper__slide--selectable': isSelectableSlide(slide),
              'iban-swiper__slide--pending': isPendingSlide(slide),
              'iban-swiper__slide--selected': isSelectableSlide(slide) && isSelected(slide.id),
            }"
            :role="isSelectableSlide(slide) ? 'button' : undefined"
            :tabindex="isSelectableSlide(slide) ? 0 : undefined"
            :aria-label="getSlideAriaLabel(slide)"
            :aria-pressed="isSelectableSlide(slide) ? isSelected(slide.id) : undefined"
            :aria-disabled="isPendingSlide(slide) ? true : undefined"
            :data-testid="`iban-swiper-slide-${slide.id}`"
            @click="handleSelectSlide(slide)"
            @keydown.enter.prevent="handleSelectSlide(slide)"
            @keydown.space.prevent="handleSelectSlide(slide)"
          >
            <IbanSwiperSlide
              :expanded="expanded"
              :variant="slide.variant"
              :currency="slide.currency"
              :account-id="slide.id"
              :label="slide.label"
              :balance="slide.balance"
              :is-pending="isPendingSlide(slide)"
              :pending-label="t('ibanSwiper.labels.setting_up_account')"
            />

            <span v-if="expanded" class="iban-swiper__indicator" />
          </swiper-slide>
        </template>
      </swiper-container>

      <div v-if="showSwiperControls" class="iban-swiper__fade" aria-hidden="true" />
    </div>

    <div :class="['iban-swiper__navigation', { 'iban-swiper__navigation--expanded': expanded }]">
      <UiButton
        :aria-label="
          expanded ? t('dashboard.aria.collapse_insights') : t('dashboard.aria.expand_insights')
        "
        type="button"
        severity="contrast"
        data-testid="dashboard-iban-swiper-toggle"
        :icon="collapsibleIcon"
        @click="toggleExpanded"
      />

      <IbanSwiperControls
        v-if="showSwiperControls"
        :expanded="expanded"
        @prev="handlePrev"
        @next="handleNext"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.iban-swiper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  gap: $static-space-1;
  padding: 0 $static-space-1;

  &--expanded {
    padding: 0;

    .iban-swiper__viewport {
      padding: 0 0 0 $static-space-4;
    }
  }

  &__viewport {
    flex: 1;
    min-width: 0;
    height: 100%;
    display: flex;
    align-items: center;
    position: relative;
  }

  &__fade {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: f(48, 72);
    z-index: 2;
    pointer-events: none;
    background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, var(--p-surface-0, #fff) 100%);
  }

  &__container {
    flex: 1;
    min-width: 0;
    height: 100%;
  }

  &__slide {
    width: 270px;
    height: auto;
    border-radius: $radius-16;
    display: flex;
    align-items: center;
    position: relative;
    background: $color-bg-widget;
    margin: $static-space-1 0;
    margin-right: 4px !important; // annoying primevue rule difficult to override
    border: 1px solid $color-border-strong;
    transition: all 0.125s ease-in;

    &--currencies {
      background: $color-bg-surface;
      border-color: transparent;
    }

    &--expanded {
      margin: $static-space-4 0;
      margin-right: 16px !important; // annoying primevue rule difficult to override
    }

    &--selectable {
      cursor: pointer;

      &:focus-visible {
        outline: 2px solid var(--p-primary-500);
        outline-offset: 2px;
      }
    }

    &--selected {
      // The tint is layered over the widget surface so card and indicator stay
      // opaque and identical even if a customer palette defines the selection
      // tint with alpha - the indicator must mask the card border it overlaps.
      border: 1px solid var(--p-primary-500);
      background: linear-gradient($color-bg-surface, $color-bg-surface) $color-bg-widget;

      .iban-swiper__indicator {
        transition: all 0.125s ease-in;
        opacity: 1;
        background: linear-gradient($color-bg-surface, $color-bg-surface) $color-bg-widget;
        border-left: 1px solid var(--p-primary-500);
        border-bottom: 1px solid var(--p-primary-500);
      }
    }

    &--pending {
      background: var(--p-surface-100);
      border-color: var(--p-surface-300);
      opacity: 0.72;
    }
  }

  &__indicator {
    opacity: 0;
    position: absolute;
    bottom: -8.6px;
    left: 50%;
    width: $static-space-4;
    height: $static-space-4;
    transform: translateX(-50%) rotate(-45deg);
    pointer-events: none;
  }

  &__skeleton {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: $space-md;
    padding: $space-md;
  }

  &__skeleton-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-md;

    &--bottom {
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: $static-space-2;
    }
  }

  &__skeleton-line {
    border-radius: $radius-sm;

    &--label {
      width: f(90, 120);
      height: $text-md;
    }

    &--chip {
      width: f(44, 64);
      height: $text-md;
    }

    &--iban {
      width: f(170, 200);
      height: $text-md;
    }

    &--balance {
      width: f(90, 120);
      height: $text-lg;
    }
  }

  &__navigation {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    flex-shrink: 0;
    gap: $static-space-4;
    padding: $static-space-4 $static-space-1;

    &--expanded {
      justify-content: space-between;
      padding: $static-space-4 $static-space-4;
      flex-direction: column;
      height: 100%;
      gap: 0;
    }

    :deep(.p-button) {
      flex-shrink: 0;
    }

    :deep(.p-button-icon) {
      font-size: $static-text-sm;
    }
  }

  &__controls {
    display: flex;
    flex-direction: column;
    gap: $static-space-1; // 4px
    flex-shrink: 0;
  }
}
</style>
